import React, { useState, useEffect, useRef } from 'react';
import { initQZ, printLabel } from '../qzHelper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack'; // Added Stack
import { useTheme } from '@mui/material/styles'; // Added useTheme
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import PercentIcon from '@mui/icons-material/Percent';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // Added for no results

// Define the BookResult interface
interface BookResult {
  id: number;
  isbn: string;
  title: string;
  author: string;
  binding_type: string;
  sales_rank: number;
  cover_url: string;
  distance: number;
  confidence: number;
  no_reliable_match?: boolean;
  llm_verification?: {
    applied: boolean;
    confidence: number;
    reasoning: string;
  };
}

// Define the ScanResult interface
interface ScanResult {
  results: BookResult[];
  bindingType: string;
  timestamp: number;
  match_status?: 'found' | 'not_found' | 'error';
  identifier?: string; // ASIN or ZB_ID
  description?: string; // Book title or LLM description
}

interface ProcessingQueueItem {
  id: number;
  imageBlob: Blob;
  timestamp: number;
  bindingType: string;
  status: 'queued' | 'processing' | 'completed' | 'error';
  retryCount: number;
}

interface ResultsSidebarProps {
  currentResults: BookResult[] | null;
  isLoading: boolean;
  scanHistory: ScanResult[];
  processingQueue: ProcessingQueueItem[];
  currentScan?: ScanResult | null; // Added to get access to the full scan result
}

const ResultsSidebar: React.FC<ResultsSidebarProps> = ({
  currentResults,
  isLoading,
  scanHistory,
  processingQueue,
  currentScan,
}) => {
  const theme = useTheme(); // Get theme object
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredScan, setHoveredScan] = useState<ScanResult | null>(null);

  const [printers, setPrinters] = useState<string[]>([]);
  const [selectedPrinterName, setSelectedPrinterName] = useState<string | null>(localStorage.getItem('selectedPrinter'));

  const lastPrintedAsinRef = useRef<string | null>(null);

  useEffect(() => {
    async function fetchPrinters() {
      await initQZ();
      const allPrinters = await import('../qzHelper').then(m => m.listPrinters());
      setPrinters(await allPrinters);
      const saved = localStorage.getItem('selectedPrinter');
      if (saved) setSelectedPrinterName(saved);
    }
    fetchPrinters();
  }, []);

  useEffect(() => {
    // Skip if we have no current scan
    if (!currentScan) return;

    // For matched books, use the identifier (ASIN) and description (title)
    // For unmatched books, use the custom ZB_ID and LLM description
    const identifier = currentScan.identifier;
    const description = currentScan.description;

    // Skip if we don't have both identifier and description
    if (!identifier || !description) return;

    // Only print if the identifier is different from the last one we printed
    if (lastPrintedAsinRef.current !== identifier) {
      printLabel(identifier, description);
      lastPrintedAsinRef.current = identifier;
    }
  }, [currentScan]);

  // Helper function to determine if a result is a strong match
  const isStrongMatch = (book: BookResult): boolean => {
    // Check if AI verification was applied
    const wasAiVerified = book.llm_verification?.applied === true;

    // Use different confidence thresholds based on whether AI verification was applied
    const confidenceThreshold = wasAiVerified ? 0.8 : 0.90;

    // Check if vector confidence is high enough based on the appropriate threshold
    const hasHighConfidence = book.confidence >= confidenceThreshold;

    // Check if AI verification found a match (absence of no_reliable_match flag)
    const aiVerified = !(book.no_reliable_match === true);

    // Both conditions must be true
    return hasHighConfidence && aiVerified;
  };

  // Helper function to get card background color based on match status using theme
  const getCardBackgroundColor = (book: BookResult): string => {
    // Handle error state explicitly
    if (book.isbn === 'error') {
      return theme.palette.warning.light; // Use warning color for errors
    }
    return isStrongMatch(book) ? theme.palette.success.light : theme.palette.error.light;
  };


  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, scan: ScanResult) => {
    // Don't open popover for error results
    if (scan.results[0]?.isbn === 'error') return;
    setAnchorEl(event.currentTarget);
    setHoveredScan(scan);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setHoveredScan(null);
  };

  const open = Boolean(anchorEl);

  const formatConfidence = (confidence: number): string => {
    return `${Math.round(confidence * 100)}%`;
  };

  const getConfidenceColor = (confidence: number, book?: BookResult): 'success' | 'warning' | 'error' => {
    // Handle error state
    if (book?.isbn === 'error') return 'warning';

    // Check if we have the book object with AI verification info
    if (book?.llm_verification?.applied === true) {
      // For AI verified results, use the original 0.8 threshold
      if (confidence >= 0.8) return 'success';
    } else {
      // For results without AI verification, use a higher threshold (0.90)
      if (confidence >= 0.90) return 'success';
    }

    // For all other cases, use the original warning/error thresholds
    if (confidence >= 0.5) return 'warning';
    return 'error';
  };

  // Helper to render icon + text pairs with explicit overflow handling
  const renderDetailItem = (icon: React.ReactNode, text: React.ReactNode, key?: string | number) => (
    <Stack direction="row" spacing={0.5} alignItems="center" key={key} sx={{ minWidth: 0, overflow: 'hidden' }}> {/* Added overflow */}
      {icon}
      {/* Apply overflow styles directly */}
      <Typography
        variant="caption"
        noWrap
        sx={{
          color: 'text.secondary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </Typography>
    </Stack>
  );

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 1.5, md: 2 }, // Responsive padding
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3, // Consistent rounding
        // Removed maxHeight constraint to allow sidebar to grow taller
        overflow: 'hidden', // Hide overflow initially
      }}
    >
      <Typography variant="h5" component="h2" gutterBottom sx={{ px: { xs: 0.5, md: 0 } }}>
        Scan Results
      </Typography>

      {/* Printer selection dropdown */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Select Printer
        </Typography>
        <select
          value={selectedPrinterName ?? ''}
          onChange={(e) => {
            const printer = e.target.value;
            setSelectedPrinterName(printer);
            import('../qzHelper').then(m => m.setPrinter(printer));
          }}
          style={{
            padding: '6px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            width: '100%',
            maxWidth: '100%',
            fontSize: '0.9rem'
          }}
        >
          <option value="" disabled>Select a printer</option>
          {printers.map((printer) => (
            <option key={printer} value={printer}>
              {printer}
            </option>
          ))}
        </select>
      </Box>

      {/* Queue Status Indicator */}
      {processingQueue.length > 0 && (
        <Paper
          elevation={0} // Use outlined variant or low elevation
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 2,
            backgroundColor: theme.palette.grey[100], // Use theme grey
            borderRadius: 2 // Consistent rounding
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Processing Queue
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {`${processingQueue.filter(item => item.status === 'processing').length} processing, ${processingQueue.filter(item => item.status === 'queued').length} queued`}
              </Typography>
            </Box>
            <Chip
              label={`${processingQueue.length} total`}
              size="small"
              color="primary"
              variant="outlined" // Use outlined chip
              sx={{ height: 24 }}
            />
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {processingQueue.map((item) => (
              <Box
                key={item.id}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: item.status === 'processing' ? theme.palette.primary.main : theme.palette.primary.light, // Use theme colors
                  animation: item.status === 'processing' ? 'pulse 1.5s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                    '100%': { opacity: 1 }
                  }
                }}
                title={`Item ${item.id} - ${item.status}`} // Add tooltip
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* Scrollable content area */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 0.5 }}>
        {/* Current scan results or loading indicator */}
        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ p: 4, minHeight: 150 }}>
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ mt: 2 }} color="text.secondary">
              Processing image...
            </Typography>
          </Stack>
        ) : currentScan ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
              Current Scan
            </Typography>
            
            {/* Handle different types of scan results */}
            {currentScan.match_status === 'not_found' ? (
              /* No Match Found Case */
              <Card
                variant="outlined"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  mb: 1,
                  position: 'relative',
                  backgroundColor: theme.palette.warning.light,
                  borderWidth: '1px',
                  borderColor: theme.palette.divider,
                  borderRadius: 2,
                }}
              >
                <Stack direction="row" spacing={1.5} sx={{ p: 1.5, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 70,
                      height: 105,
                      borderRadius: 1,
                      bgcolor: 'grey.200',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography variant="h4" sx={{ color: 'text.secondary' }}>?</Typography>
                  </Box>
                  <Box sx={{ flex: '1 0 auto', minWidth: 0 }}>
                    <Typography variant="subtitle1" component="div" noWrap sx={{ fontWeight: 'medium', mb: 0.5 }}>
                      {currentScan.description || 'Unrecognized Book'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 1 }}>
                      No Match Found
                    </Typography>

                    <button
                      onClick={() => {
                        if (currentScan.identifier && currentScan.description) {
                          printLabel(currentScan.identifier, currentScan.description);
                        }
                      }}
                      style={{
                        marginBottom: '8px',
                        padding: '4px 8px',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        background: '#f5f5f5'
                      }}
                    >
                      Print Label
                    </button>

                    <Grid container spacing={0.5} rowSpacing={1}>
                      <Grid item xs={12} sm={6}>
                        {renderDetailItem(
                          <MenuBookIcon sx={{ fontSize: 16, color: 'text.secondary' }} />,
                          currentScan.identifier || 'No ID'
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {renderDetailItem(
                          <LocalLibraryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />,
                          currentScan.bindingType || 'Unknown'
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Chip
                          label="No Match"
                          color="warning"
                          size="small"
                          sx={{ height: 20, '& .MuiChip-label': { px: 1, py: 0 } }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Stack>
              </Card>
            ) : (
              /* Match Found Case */
              currentResults && currentResults.length > 0 && (
                <Card
                  variant="outlined"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    mb: 1,
                    position: 'relative',
                    backgroundColor: getCardBackgroundColor(currentResults[0]),
                    borderWidth: '1px',
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                  }}
                >
                  <Stack direction="row" spacing={1.5} sx={{ p: 1.5, alignItems: 'flex-start' }}>
                    <CardMedia
                      component="img"
                      sx={{
                        width: 70,
                        height: 105,
                        objectFit: 'contain',
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                      }}
                      image={currentResults[0].cover_url || undefined}
                      alt={currentResults[0].title}
                    />
                    <Box sx={{ flex: '1 0 auto', minWidth: 0 }}>
                      <Typography variant="subtitle1" component="div" noWrap sx={{ fontWeight: 'medium', mb: 0.5 }}>
                        {currentResults[0].title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 1 }}>
                        {currentResults[0].author}
                      </Typography>

                      <button
                        onClick={() => {
                          if (currentScan.identifier && currentScan.description) {
                            printLabel(currentScan.identifier, currentScan.description);
                          } else if (currentResults && currentResults[0]) {
                            printLabel(currentResults[0].isbn, currentResults[0].title);
                          }
                        }}
                        style={{
                          marginBottom: '8px',
                          padding: '4px 8px',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                          background: '#f5f5f5'
                        }}
                      >
                        Print Label
                      </button>

                      <Grid container spacing={0.5} rowSpacing={1}>
                        <Grid item xs={12} sm={6}>
                          {renderDetailItem(
                            <MenuBookIcon sx={{ fontSize: 16, color: 'text.secondary' }} />,
                            currentResults[0].isbn
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          {renderDetailItem(
                            <LocalLibraryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />,
                            currentResults[0].binding_type
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <PercentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Chip
                              label={formatConfidence(currentResults[0].confidence)}
                              color={getConfidenceColor(currentResults[0].confidence, currentResults[0])}
                              size="small"
                              sx={{ height: 20, '& .MuiChip-label': { px: 1, py: 0 } }}
                            />
                          </Stack>
                        </Grid>
                        {currentResults[0].sales_rank > 0 && (
                          <Grid item xs={12} sm={6}>
                            {renderDetailItem(
                              <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />,
                              `Rank: ${currentResults[0].sales_rank.toLocaleString()}`
                            )}
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Stack>
                </Card>
              )
            )}
          </Box>
        ) : !isLoading && !currentResults && scanHistory.length === 0 && processingQueue.length === 0 ? (
          // Show message when there are no results and nothing is loading/queued
          <Stack alignItems="center" justifyContent="center" sx={{ p: 4, minHeight: 150, textAlign: 'center' }}>
            <InfoOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Scan or upload an image to see results here.
            </Typography>
          </Stack>
        ) : null}

        {/* Divider between current and history */}
        {((currentResults && currentResults.length > 0) || isLoading) && scanHistory.length > 0 && (
          <Divider sx={{ my: 1.5 }} />
        )}

        {/* Scan history */}
        {scanHistory.length > 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
              Previous Scans
            </Typography>
            {scanHistory.map((scan) => {
              // Handle case where scan.results is empty array (no matches found)
              const hasResults = scan.results && scan.results.length > 0;
              const isNoMatch = scan.match_status === 'not_found';
              
              // For no match scenarios, create a placeholder result with all required BookResult properties
              const displayResult = hasResults ? scan.results[0] : {
                id: -1, // Add missing required property
                isbn: 'no_match',
                title: scan.description || 'No Match Found',
                author: 'Unrecognized Book',
                binding_type: scan.bindingType || 'unknown',
                cover_url: '',
                confidence: 0,
                sales_rank: 0,
                distance: 0, // Add missing required property
                no_reliable_match: true
              };
              
              return (
                <Card
                  key={scan.timestamp}
                  variant="outlined" // Use outlined card
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    mb: 1.5, // Consistent margin
                    position: 'relative',
                    cursor: hasResults && displayResult.isbn !== 'error' ? 'pointer' : 'default', // Only pointer if not error
                    backgroundColor: hasResults ? getCardBackgroundColor(displayResult) : theme.palette.warning.light,
                    borderWidth: '1px',
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                    '&:hover': { // Subtle hover effect
                      borderColor: hasResults && displayResult.isbn !== 'error' ? theme.palette.primary.main : theme.palette.divider,
                      boxShadow: hasResults && displayResult.isbn !== 'error' ? 1 : 0,
                    }
                  }}
                  onMouseEnter={(e) => hasResults ? handlePopoverOpen(e, scan) : null}
                  onMouseLeave={handlePopoverClose}
                >
                  {/* Conditionally render either a CardMedia with image or a Box with placeholder text */}
                  {hasResults ? (
                    <CardMedia
                      component="img"
                      sx={{
                        width: 50, // Smaller image for history
                        height: 75, // Smaller image for history
                        objectFit: 'contain',
                        m: 1, // Margin around image
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                      }}
                      image={displayResult.cover_url || undefined}
                      alt={displayResult.title}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 50, // Match width of image
                        height: 75, // Match height of image
                        m: 1, // Match margin of image
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography variant="caption" component="div" sx={{ fontSize: '2rem', color: 'text.secondary' }}>
                        ?
                      </Typography>
                    </Box>
                  )}
                  {/* Ensure CardContent allows truncation */}
                  <CardContent sx={{ flex: '1 1 auto', p: 1, '&:last-child': { pb: 1 }, minWidth: 0, overflow: 'hidden' }}>
                    {/* Title with explicit overflow */}
                    <Typography
                      variant="subtitle2"
                      component="div"
                      noWrap
                      sx={{
                        fontWeight: 'medium',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mb: 0.5, // Add margin bottom
                      }}
                    >
                      {displayResult.title}
                    </Typography>
                    <button
                      onClick={() => {
                        // Use scan identifier and description if available, otherwise fall back to results
                        if (scan.identifier && scan.description) {
                          printLabel(scan.identifier, scan.description);
                        } else if (hasResults) {
                          printLabel(displayResult.isbn, displayResult.title);
                        }
                      }}
                      style={{
                        marginBottom: '4px',
                        padding: '2px 6px',
                        fontSize: '0.7rem',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        background: '#f5f5f5'
                      }}
                    >
                      Print Label
                    </button>
                    {/* Use Flexbox for details layout */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', minWidth: 0 }}>
                      {/* Left side: Author & Binding */}
                      <Box sx={{ minWidth: 0, mr: 1 }}> {/* Allow shrinking, add margin */}
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}> {/* Use block for potential wrap if needed, but noWrap takes precedence */}
                          {displayResult.author}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                          {displayResult.binding_type}
                        </Typography>
                      </Box>
                      {/* Right side: ISBN & Chip */}
                      <Box sx={{ flexShrink: 0 }}> {/* Prevent shrinking */}
                        <Stack spacing={0.2} alignItems="flex-end">
                          {/* renderDetailItem handles its own overflow */}
                          {renderDetailItem(
                            <MenuBookIcon sx={{ fontSize: 14, color: 'text.secondary' }} />,
                            isNoMatch ? scan.identifier || 'No Match' : displayResult.isbn,
                            `hist-isbn-${scan.timestamp}`
                          )}
                          {!isNoMatch && (
                            <Chip
                              label={formatConfidence(displayResult.confidence)}
                              color={getConfidenceColor(displayResult.confidence, displayResult)}
                              size="small"
                              sx={{ height: 18, '& .MuiChip-label': { px: 0.8, py: 0, fontSize: '0.7rem' } }}
                            />
                          )}
                          {isNoMatch && (
                            <Chip
                              label="No Match"
                              color="warning"
                              size="small"
                              sx={{ height: 18, '& .MuiChip-label': { px: 0.8, py: 0, fontSize: '0.7rem' } }}
                            />
                          )}
                        </Stack>
                      </Box> {/* Close the right side Box */}
                    </Box> {/* Close the flex Box */}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Hover preview popover */}
      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: 'none', // Let mouse events pass through
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        onClose={handlePopoverClose}
        // disableRestoreFocus // Removed to allow MUI default focus management
        PaperProps={{
          sx: {
            ml: -1, // Adjust position slightly
            borderRadius: 2, // Consistent rounding
            boxShadow: 3, // Add shadow
          }
        }}
      >
        {/* Popover Content */}
        <Box sx={{ p: 1.5, maxWidth: 350 }}> {/* Reduced padding and max width */}
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>
            Top 5 Matches
          </Typography>
          {hoveredScan?.results.slice(0, 5).map((book, index) => (
              <Stack
                key={book.id || index}
                direction="row"
                spacing={1} // Spacing between image and details
                sx={{
                  mb: index < 4 ? 1 : 0, // Margin bottom except for last item
                  pb: index < 4 ? 1 : 0, // Padding bottom except for last item
                  borderBottom: index < 4 ? `1px solid ${theme.palette.divider}` : 'none',
                }}
              >
                <CardMedia
                  component="img"
                  sx={{
                    width: 35, // Smaller image in popover
                    height: 52, // Smaller image in popover
                    objectFit: 'contain',
                    borderRadius: 1,
                    bgcolor: 'grey.200',
                    flexShrink: 0, // Prevent image shrinking
                  }}
                  image={book.cover_url || undefined}
                  alt={book.title}
                />
                <Box sx={{ minWidth: 0 }}> {/* Allow text to wrap/truncate */}
                  <Typography variant="body2" component="div" noWrap sx={{ fontWeight: 'medium' }}>
                    {book.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 0.5 }}>
                    {book.author}
                  </Typography>

                  {/* Use Grid for details layout in popover - Reordered */}
                  <Grid container spacing={0.5} rowSpacing={0.5}>
                    {/* Row 1: ISBN and Sales Rank */}
                    <Grid item xs={6}>
                      {renderDetailItem(
                        <MenuBookIcon sx={{ fontSize: 14, color: 'text.secondary' }} />,
                        book.isbn
                      )}
                    </Grid>
                    {book.sales_rank > 0 && (
                      <Grid item xs={6}>
                        {renderDetailItem(
                          <SearchIcon sx={{ fontSize: 14, color: 'text.secondary' }} />,
                          book.sales_rank.toLocaleString()
                        )}
                      </Grid>
                    )}
                    {/* Row 2: Binding Type and Confidence */}
                    <Grid item xs={6}>
                      {renderDetailItem(
                        <LocalLibraryIcon sx={{ fontSize: 14, color: 'text.secondary' }} />,
                        book.binding_type
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <PercentIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Chip
                          label={formatConfidence(book.confidence)}
                          color={getConfidenceColor(book.confidence, book)}
                          size="small"
                          sx={{ height: 18, '& .MuiChip-label': { px: 0.8, py: 0, fontSize: '0.7rem' } }}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
          ))}
        </Box>
      </Popover>
    </Paper>
  );
};

export default ResultsSidebar;
